import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";

export default function About() {
  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#3D4B59]">
      <Helmet>
        <title>About Us - Cosmovex</title>
        <meta
          name="description"
          content="Learn about Cosmovex - a technology company dedicated to creating innovative mobile apps and sharing tech insights."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cosmovexapps.com/about" />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FBFAF6]/80 backdrop-blur-xl border-b border-[#E1DCCE]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-[#E8B44C] border border-[#D9A32E] rounded-xl flex items-center justify-center shadow-sm transition-shadow">
                <span className="text-lg font-black text-[#221A06]">C</span>
              </div>
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#26313B] group-hover:text-[#A8720E] transition-colors">
                Cosmovex
              </h1>
              <p className="text-xs text-[#8A93A2] font-mono">// Tech Insights</p>
            </div>
          </Link>

          <nav className="flex items-center gap-2">
            <Link
              to="/"
              className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
            >
              Home
            </Link>
            <Link
              to="/blogs"
              className="px-4 py-2 text-sm text-[#5B6B7C] hover:text-[#26313B] hover:bg-[#F1EEE5] rounded-lg transition-all"
            >
              Blogs
            </Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-[#26313B]">About Cosmovex</h1>
          <p className="text-xl text-[#A8720E] mb-12">
            Where Ideas Meet Innovation
          </p>

          <div className="prose max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Who We Are</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Cosmovex is a technology company dedicated to creating innovative mobile applications and sharing valuable tech insights. We're a small team with big standards, obsessing over latency, clarity, and calm design.
              </p>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                Based in India, we craft mobile apps that feel magical—fast, focused, and human. Every screen earns its place, every tap respects your time. Our apps are built to get out of your way and help you win your day.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">What We Do</h2>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white border border-[#E1DCCE] rounded-lg p-6 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
                  <div className="w-12 h-12 bg-[#E8B44C] text-[#221A06] rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-[#26313B]">Mobile Apps</h3>
                  <p className="text-[#5B6B7C]">
                    We build performance-first, privacy-respectful mobile applications that solve real problems with elegant solutions.
                  </p>
                </div>

                <div className="bg-white border border-[#E1DCCE] rounded-lg p-6 shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
                  <div className="w-12 h-12 bg-[#E8B44C] text-[#221A06] rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-[#26313B]">Tech Blog</h3>
                  <p className="text-[#5B6B7C]">
                    We share cutting-edge tech insights, AI developments, and software engineering tutorials to help developers stay ahead.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Our Values</h2>
              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#F1EEE5] border border-[#E1DCCE] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-[#A8720E] font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#26313B] mb-1">Performance First</h3>
                    <p className="text-[#5B6B7C]">We obsess over latency and optimize every interaction to be lightning-fast.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#F1EEE5] border border-[#E1DCCE] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-[#A8720E] font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#26313B] mb-1">Privacy Respectful</h3>
                    <p className="text-[#5B6B7C]">Your data is yours. We build with privacy and security at the core.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#F1EEE5] border border-[#E1DCCE] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-[#A8720E] font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#26313B] mb-1">Design for Flow</h3>
                    <p className="text-[#5B6B7C]">From onboarding to daily use, we design for fewer steps, clearer actions, and faster outcomes.</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-[#F1EEE5] border border-[#E1DCCE] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-[#A8720E] font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#26313B] mb-1">Knowledge Sharing</h3>
                    <p className="text-[#5B6B7C]">We believe in sharing what we learn through detailed tutorials and insights.</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Our Products</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-4">
                We've developed several mobile applications available on the Google Play Store and Apple App Store:
              </p>
              <ul className="space-y-3 text-[#3D4B59]">
                <li className="flex items-start gap-3">
                  <span className="text-[#A8720E] mt-1">•</span>
                  <span><strong className="text-[#26313B]">IF Tracker</strong> - Intermittent fasting tracker to help you achieve your health goals</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#A8720E] mt-1">•</span>
                  <span><strong className="text-[#26313B]">PDF AI Toolkit</strong> - Powerful PDF tools powered by AI for editing, converting, and managing PDFs</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#A8720E] mt-1">•</span>
                  <span><strong className="text-[#26313B]">Easy Khata</strong> - Simple accounting and bookkeeping solution for small businesses</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4 text-[#26313B]">Get in Touch</h2>
              <p className="text-[#3D4B59] leading-relaxed mb-6">
                We'd love to hear from you! Whether you have questions, feedback, or just want to say hi, feel free to reach out.
              </p>

              <div className="bg-[#F1EEE5] border border-[#E1DCCE] rounded-lg p-8">
                <h3 className="text-xl font-semibold mb-4 text-[#26313B]">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white border border-[#E1DCCE] rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#A8720E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-[#5B6B7C]">Email</p>
                      <a href="mailto:contact@cosmovex.com" className="text-[#0F6BBF] hover:text-[#0C599F] font-medium">
                        contact@cosmovex.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white border border-[#E1DCCE] rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#A8720E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-[#5B6B7C]">Website</p>
                      <a href="https://cosmovexapps.com" className="text-[#0F6BBF] hover:text-[#0C599F] font-medium">
                        cosmovexapps.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white border border-[#E1DCCE] rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#A8720E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-[#5B6B7C]">Location</p>
                      <p className="text-[#26313B] font-medium">India</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <div className="bg-white border border-[#E1DCCE] rounded-lg p-8 text-center shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05)]">
                <h3 className="text-2xl font-bold mb-4 text-[#26313B]">Stay Connected</h3>
                <p className="text-[#3D4B59] mb-6">
                  Subscribe to our newsletter for the latest tech insights and product updates.
                </p>
                <Link
                  to="/#newsletter"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#E8B44C] border border-[#D9A32E] text-[#221A06] font-semibold rounded-lg hover:bg-[#DEA83B] transition-all"
                >
                  Subscribe to Newsletter
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </section>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E1DCCE] py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#8A93A2] text-sm">
            &copy; {new Date().getFullYear()} Cosmovex. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/privacy-policy" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              Terms of Service
            </Link>
            <Link to="/about" className="text-[#5B6B7C] hover:text-[#A8720E] text-sm">
              About
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
